// Puts the configured render on the checkout line thumbnail.
//
// The cart transform (hub-api-shopify, expivi-dynamic-price) can only use an
// image hosted on Shopify's CDN, and the hub copies each render there in the
// background after add-to-cart (seconds, never on the add-to-cart path). This
// polls for that copy and writes it onto the line as `_preview_cdn`, which the
// transform reads. A line without it just keeps its variant photo at checkout.
//
// Runs on the cart page and right after an add-to-cart. Rewriting a line's
// properties changes its key, and the cart page was rendered with the old one
// (quantity and remove controls send it). Rather than reloading the page, the
// old key is swapped for the new one wherever it appears on the page; if the new
// key cannot be found, it falls back to a reload.
(function () {
    var RENDER_HASH = /\/api\/renders\/\d+x\d+\/([a-f0-9]{64})\.png$/;
    var CDN_URL = /^https:\/\/(cdn\.shopify\.com|cdn\.shopifycdn\.net)\//;
    var POLL_MS = 2000;
    var POLL_TRIES = 20; // ~40s, then the next cart page load tries again

    /** Lines that carry a render but no CDN copy yet: [{ key, previewUrl, hash }]. Pure. */
    function pendingLines(cart) {
        return ((cart && cart.items) || []).reduce(function (out, item) {
            var props = item.properties || {};
            var match = RENDER_HASH.exec(typeof props._preview_url === 'string' ? props._preview_url : '');
            if (match && !props._preview_cdn) {
                out.push({ key: item.key, previewUrl: props._preview_url, hash: match[1] });
            }
            return out;
        }, []);
    }

    /**
     * `value` with a line key swapped for another. A key (`<id>:<hash>`) sits in
     * attributes raw (data-cart-item-key) and URL-encoded (a /cart/change?id= link),
     * so both forms are replaced. Pure.
     */
    function replaceKey(value, oldKey, newKey) {
        return String(value)
            .split(oldKey).join(newKey)
            .split(encodeURIComponent(oldKey)).join(encodeURIComponent(newKey));
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = { pendingLines: pendingLines, CDN_URL: CDN_URL, replaceKey: replaceKey };
    }
    if (typeof document === 'undefined') {
        return;
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var busy = false;

    function readCart() {
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
    }

    function lookup(hash) {
        // App-proxy paths are absolute, never locale-prefixed.
        return fetch('/apps/hub/renders/' + hash + '/cdn', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (b) { return b && b.success && CDN_URL.test(b.url) ? b.url : null; })
            .catch(function () { return null; });
    }

    function waitFor(hash, tries) {
        return lookup(hash).then(function (url) {
            if (url || tries <= 1) {
                return url;
            }
            return new Promise(function (resolve) { setTimeout(resolve, POLL_MS); })
                .then(function () { return waitFor(hash, tries - 1); });
        });
    }

    // Re-read the cart right before writing: the shopper may have changed it
    // during the wait, and a stale key would 404 or hit the wrong line.
    //
    // Resolves { oldKey, newKey } on success (newKey is null if the response did
    // not say which line it became), or null when nothing was written.
    function write(line, url) {
        return readCart().then(function (cart) {
            var current = ((cart && cart.items) || []).filter(function (item) {
                return (item.properties || {})._preview_url === line.previewUrl
                    && !(item.properties || {})._preview_cdn;
            })[0];
            if (!current) {
                return null;
            }
            return fetch(root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    id: current.key,
                    quantity: current.quantity,
                    properties: Object.assign({}, current.properties, { _preview_cdn: url }),
                }),
            }).then(function (r) {
                if (!r.ok) {
                    return null;
                }
                return r.json().then(function (updated) {
                    var next = ((updated && updated.items) || []).filter(function (item) {
                        return (item.properties || {})._preview_url === line.previewUrl
                            && (item.properties || {})._preview_cdn === url;
                    })[0];
                    return { oldKey: current.key, newKey: next ? next.key : null };
                }, function () { return { oldKey: current.key, newKey: null }; });
            }, function () { return null; });
        });
    }

    // Swap an old line key for its new one everywhere on the page: data-cart-item-key,
    // the quantity controls' data-line-key and their /cart/change?id= links. Swept
    // by value rather than by attribute name, so it does not depend on the markup.
    function swapKey(oldKey, newKey) {
        Array.prototype.forEach.call(document.body.querySelectorAll('*'), function (el) {
            Array.prototype.forEach.call(el.attributes, function (attr) {
                if (attr.value.indexOf(oldKey) !== -1 || attr.value.indexOf(encodeURIComponent(oldKey)) !== -1) {
                    el.setAttribute(attr.name, replaceKey(attr.value, oldKey, newKey));
                }
            });
        });
    }

    function run() {
        if (busy) {
            return;
        }
        busy = true;
        readCart().then(function (cart) {
            return pendingLines(cart).reduce(function (chain, line) {
                return chain.then(function (swaps) {
                    return waitFor(line.hash, POLL_TRIES)
                        .then(function (url) { return url ? write(line, url) : null; })
                        .then(function (swap) { return swap ? swaps.concat([swap]) : swaps; });
                });
            }, Promise.resolve([]));
        }).then(function (swaps) {
            busy = false;
            if (!swaps.length) {
                return;
            }
            if (document.querySelector('.hs-cart-grid')) {
                // Fresh keys in place, no reload. A write whose new key we could not
                // read leaves the page with a stale key, so only then reload.
                if (swaps.some(function (s) { return !s.newKey; })) {
                    window.location.reload();
                    return;
                }
                swaps.forEach(function (s) { swapKey(s.oldKey, s.newKey); });
            } else {
                document.dispatchEvent(new CustomEvent('cart:refresh'));
            }
        }, function () { busy = false; });
    }

    document.addEventListener('expivi:added-to-cart', run);
    if (document.querySelector('.hs-cart-grid')) {
        run();
    }
})();
